'use client';

import { Chart, useChart } from '@chakra-ui/charts';
import { Flex, GridItem, Heading, Image, SimpleGrid, Stack, Text } from '@chakra-ui/react';
import ChartIntervals, { ChartInterval } from '@components/common/ChartIntervals';
import ShareButton from '@components/common/ShareButton';
import { getIntervalLabel } from '@features/explore/constants';
import ContractTable from '@features/explore/ContractTable';
import EnterPosition from '@features/explore/EnterPosition';
import MarketData from '@features/explore/MarketData';
import TradingActivity from '@features/explore/TradingActivity';
import { useBreadcrumbLabel } from '@features/navigation/BreadcrumbContext';
import { useGetCommodity } from 'hooks/useGetCommodity';
import { useHref } from 'hooks/useHref';
import { memo, useCallback, useState } from 'react';
import { Area, AreaChart, Tooltip, XAxis, YAxis } from 'recharts';

interface Props {
  commodityId: string;
}

const INTERVALS: ChartInterval[] = ['1D', '1W', '1M'];
const DepositsChart = () => {
  const [interval, setInterval] = useState<ChartInterval>('1D');
  const chart = useChart({
    data: [
      { deposits: 10000, month: 'January' },
      { deposits: 200000, month: 'February' },
      { deposits: 30000, month: 'March' },
      { deposits: 40000, month: 'May' },
      { deposits: 80000, month: 'June' },
      { deposits: 90000, month: 'August' },
      { deposits: 19900, month: 'October' },
      { deposits: 185000, month: 'November' },
    ],
    series: [{ name: 'deposits', color: 'orange.solid' }],
  });

  const handleIntervalChange = useCallback((interval: ChartInterval) => {
    setInterval(interval);
  }, []);

  return (
    <Flex direction="column" gap={4} p={1}>
      <Stack
        direction={{ base: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems="center"
      >
        <Flex gap={4} alignItems="center">
          <Flex flexDirection="column" gap={2}>
            <Text fontSize="sm" color="text.secondary">
              Total commitments
            </Text>
            <Heading size="3xl">GHS100,000</Heading>
          </Flex>
          <Flex direction="column">
            <Text fontSize="xs" color="red.500">
              -GHS1,000(-0.1%)
            </Text>
            <Text fontSize="sm" color="text.secondary">
              {getIntervalLabel(interval)}
            </Text>
          </Flex>
        </Flex>
        <ChartIntervals
          onChange={handleIntervalChange}
          defaultValue={interval}
          intervals={INTERVALS}
        />
      </Stack>
      <Chart.Root maxH="xs" chart={chart}>
        <AreaChart accessibilityLayer data={chart.data}>
          <XAxis
            dataKey={chart.key('month')}
            tickMargin={8}
            tickFormatter={(value) => value.slice(0, 3)}
            stroke={chart.color('border.light')}
          />
          <YAxis stroke={chart.color('border.light')} />
          <Tooltip cursor={false} animationDuration={100} content={<Chart.Tooltip />} />
          {chart.series.map((item) => (
            <Area
              type="natural"
              key={item.name}
              isAnimationActive={false}
              dataKey={chart.key(item.name)}
              fill={chart.color(item.color)}
              fillOpacity={0.2}
              stroke={chart.color(item.color)}
              stackId="a"
            />
          ))}
        </AreaChart>
      </Chart.Root>
    </Flex>
  );
};

const CommodityDetailPage = ({ commodityId }: Props) => {
  const { commodity } = useGetCommodity(commodityId);
  const { commodityHref } = useHref();

  useBreadcrumbLabel('Commodity');

  if (commodity == null) {
    return (
      <Flex>
        <Text>Commodity not found</Text>
      </Flex>
    );
  }

  const href = commodityHref(commodityId);
  return (
    <Flex direction="column" gap={5} p={4} h="fit-content">
      <Flex direction="row" gap={2} alignItems="center">
        <Image src={commodity.image} alt={commodity.name} boxSize="64px" />
        <Heading size="2xl">{commodity.name}</Heading>
        <ShareButton
          ariaLabel="Share commodity"
          size="xs"
          variant="secondaryOutline"
          ml="auto"
          borderRadius="full"
          href={href}
        />
      </Flex>
      <SimpleGrid columns={{ base: 1, md: 4 }} gap={4}>
        <GridItem colSpan={{ base: 1, md: 3 }} bg="surface.muted" px={4} py={5} borderRadius="xl">
          <DepositsChart />
        </GridItem>
        <GridItem colSpan={{ base: 1, md: 1 }}>
          <EnterPosition commodityId={commodityId} />
        </GridItem>
      </SimpleGrid>
      <ContractTable />
      <MarketData />
      <TradingActivity />
    </Flex>
  );
};

export default memo(CommodityDetailPage);
